import { LoggerService } from '@angular-ru/cdk/logger';
import { inject, Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { icon, IconDefinition, Transform } from '@fortawesome/fontawesome-svg-core';
import { FabIconMap } from './awesome/fab.icons';
import { FarIconMap } from './awesome/far.icons';
import { FasIconMap } from './awesome/fas.icons';

/**
 * Every supported icon, name → FontAwesome definition.
 * The 'far:' / 'fas:' / 'fab:' prefixes keep the keys collision-free.
 */
const SupportedIconMap = { ...FarIconMap, ...FasIconMap, ...FabIconMap };

/**
 * Union of every icon name the system supports via JSON.
 */
export type SupportedIconNames = keyof typeof SupportedIconMap;

const SupportedIconNamesSet: ReadonlySet<string> = new Set(Object.keys(SupportedIconMap));

/**
 * Type guard: is this (JSON-sourced) string the name of a registered icon?
 *
 * Pure — no logging or other side effects — so callers decide how to react
 * to an unsupported name.
 */
export function isSupportedIconName(value: string): value is SupportedIconNames {
  return SupportedIconNamesSet.has(value);
}

/**
 * Per-icon optical adjustments — a Transform with just the fields that need to
 * change vs. the FA "no-op" defaults. Icons not listed here skip the FA
 * transform pipeline entirely (see why in initialize()).
 */
const OpticalAdjustments: Partial<Record<SupportedIconNames, Transform>> = {
  // Play buttons have an optical illusion where mathematically centered doesn't
  // look good — shift the play right by 1.
  'fas:play': { x: 1 },
  'fas:circle-play': { x: 1 },
  // The magnifier's handle pulls the visual mass up-and-left of the glyph's
  // bounding box, so it reads high inside a vertically centered slot (e.g. a
  // mat-form-field prefix). Nudge down.
  'fas:magnifying-glass': { y: 3 },
};

/**
 * The Icon Service converts FontAwesome Icons into Material Icon Format.
 */
@Injectable({
  providedIn: 'root',
})
export class IconService {
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly loggerService = inject(LoggerService);
  private readonly fontAwesomeLibrary = inject(FaIconLibrary);

  /**
   * Initialize the Icon Service by registering all FontAwesome icons
   * with both Material Icon Registry and FontAwesome Library.
   *
   * Called exactly once at bootstrap via provideAppInitializer (see main.ts).
   *
   * Failure is deliberately non-fatal: an app initializer that throws aborts
   * the entire bootstrap, and missing icons should never take the app down —
   * so errors are logged and swallowed instead.
   */
  initialize(): void {
    const iconEntries = Object.entries(SupportedIconMap) as [SupportedIconNames, IconDefinition][];

    try {
      this.loggerService.groupCollapsed('[IconService] Registry');
      try {
        for (const [name, iconDefinition] of iconEntries) {
          const adjustment = OpticalAdjustments[name];

          // Two important details about how this transform is constructed:
          //
          //  1. Pass the *full* FA transform when adjusting (`size`, `rotate`, `flipX`, `flipY`
          //     all explicit). FA's `generateAbstractTransformGrouping` composes the inner
          //     `<g>` transform as `translate(x*32, y*32) scale(size/16 * ±1, size/16 * ±1)
          //     rotate(rotate 0 0)` — leaving any of those fields undefined yields
          //     `scale(NaN, NaN)` / `rotate(undefined ...)`, and browsers handle invalid
          //     pieces in an SVG transform list inconsistently (Chrome may ignore just the bad
          //     piece, Firefox/Safari may invalidate the whole transform). Spelling every
          //     field out keeps the nudge deterministic across browsers.
          //
          //  2. When there's no adjustment, do NOT pass `transform: {}`. FA's
          //     `transformIsMeaningful({})` returns true (every undefined !== the 16/0/0
          //     defaults), which routes the icon through the transform pipeline anyway, with
          //     all-undefined values flowing into NaN scale/rotate. Skip the option entirely
          //     so FA falls back to the no-transform fast path.
          const transform = adjustment ? { size: 16, x: 0, y: 0, rotate: 0, flipX: false, flipY: false, ...adjustment } : undefined;

          const icn = icon(iconDefinition, {
            classes: ['fa-fw'],
            ...(transform ? { transform } : {}),
          });

          const svg = this.sanitizer.bypassSecurityTrustHtml(icn.html[0]);
          this.loggerService.log(name, icn);

          // register the icon with material
          this.iconRegistry.addSvgIconLiteralInNamespace(iconDefinition.prefix, iconDefinition.iconName, svg);
        }
      } finally {
        // close the group even on failure, so the error below (and everything
        // after it) doesn't get swallowed into the collapsed group
        this.loggerService.close();
      }

      // register all icons with fontawesome
      this.fontAwesomeLibrary.addIcons(...iconEntries.map(([, iconDefinition]) => iconDefinition));
    } catch (error) {
      this.loggerService.error('[IconService] Failed to initialize icons', error);
    }
  }
}
